import type { LeakageItem, LeakageReport, PLReport } from './types.js';
import { fmtInt, fmtMoney } from './format.js';

/**
 * Pure presenters for the redesign's Stats hero and Costs tab, so the numbers
 * a tile shows are decided in one tested place and never in JSX.
 */

/** The "Leakage at stake" tile: real money only, the estimate named apart. */
export function leakageTile(r: LeakageReport | undefined): { value: string; count: string; sub: string } {
  if (!r) return { value: '—', count: '', sub: 'loading…' };
  const real = r.items.filter((i) => !i.estimate).length;
  const est = r.items.filter((i) => i.estimate).length;
  const count = real + est === 0 ? 'nothing found' : `${real + est} item${real + est === 1 ? '' : 's'}`;
  const parts: string[] = [];
  if (r.counts['no-revenue']) parts.push('spend with no revenue');
  if (r.counts['unconfirmed-link']) parts.push('unconfirmed links');
  if (r.counts['unattributed-bill']) parts.push('unattributed bill');
  if (r.counts['missing-stats']) parts.push(`missing stats (${fmtMoney(r.estimated)} est., not counted)`);
  const sub = parts.length ? `${parts.join(' · ')} — inside the operating net, not on top of it` : 'every confirmed dollar of spend earned, every day has stats';
  return { value: fmtMoney(r.total), count, sub };
}

/** How a leakage row names its action button. */
export function leakageAction(i: LeakageItem): string {
  switch (i.action.kind) {
    case 'matching':
      return 'Confirm on Matching';
    case 'sources':
      return 'Open source';
    case 'costs':
      return 'Open Costs';
    default:
      return 'Drill down';
  }
}

export function leakageTag(kind: LeakageItem['kind']): { text: string; cls: string } {
  switch (kind) {
    case 'no-revenue':
      return { text: 'traffic, no revenue', cls: 'pill bad' };
    case 'unconfirmed-link':
      return { text: 'unconfirmed link', cls: 'pill gold' };
    case 'missing-stats':
      return { text: 'missing stats (est.)', cls: 'pill bad' };
    default:
      return { text: 'unattributed bill', cls: 'pill' };
  }
}

export interface CostTile {
  label: string;
  value: string;
  sub: string;
  tone?: 'hero' | 'accent' | undefined;
}

/**
 * The Costs tab's tiles from the P&L: the operating cost as the sum of its
 * parts, then each part with how much of the window it covers. A source that
 * is not connected says so instead of printing $0.00; other costs appear only
 * once an entry exists.
 */
export function costTiles(pl: PLReport | undefined): CostTile[] {
  if (!pl) return [];
  const o = pl.operating;
  const und = pl.undetect;
  const h = pl.hosting;
  const ov = pl.overheads;
  const windowDays = pl.coverage?.windowDays ?? pl.days;
  const ingested = pl.ingestedCost?.semantics === 'real' ? o.ingestedCost : 0;
  const total = o.cost + o.hostingCost + (ov?.otherOverheads ?? 0) + ingested + (o.launchCost ?? 0);
  const tiles: CostTile[] = [
    {
      label: `Total cost · ${pl.days > 0 ? `${pl.days}d` : pl.days === 0 ? 'all time' : 'day'}`,
      value: fmtMoney(total),
      sub:
        `the operating cost — ${und ? 'launcher bill' : 'Airman spend'}` +
        (h ? ' + hosting' : '') +
        (ov && ov.otherOverheads > 0 ? ' + other costs' : '') +
        (ingested ? ' + ingested cost' : '') +
        (o.launchCost ? ' + the modelled launch line' : ''),
      tone: 'hero',
    },
    und
      ? {
          label: 'Undetect bill',
          value: fmtMoney(und.totalUsd),
          sub:
            `${fmtInt(und.launches)} launches · ${und.gb.toLocaleString('en-US', { maximumFractionDigits: 1 })} GB · ${und.coveredDays}/${windowDays} days mirrored` +
            (und.unmirroredAirmanCost ? ` · +${fmtMoney(und.unmirroredAirmanCost)} Airman on unmirrored days` : '') +
            (und.aiCost > 0 ? ` · +${fmtMoney(und.aiCost)} Airman AI on top` : ''),
        }
      : { label: 'Undetect bill', value: 'not connected', sub: `Airman's allocation is the cost basis: ${fmtMoney(o.airmanCost)}` },
    h
      ? {
          label: 'Civo hosting',
          value: fmtMoney(h.cost),
          sub: `${h.days}/${windowDays} days mirrored · ${h.vatPct > 0 ? `incl. ${h.vatPct}% VAT` : 'ex-VAT'}` + (h.unpricedHours > 0 ? ` · ${fmtInt(h.unpricedHours)} h unpriced` : ''),
        }
      : { label: 'Civo hosting', value: 'not connected', sub: 'hosting is not charged until Civo is synced' },
    // Other costs only when something is entered: a $0.00 tile for a cost
    // nobody has is noise (2026-09-14, owner's call); the P&L rows already
    // hide the same way.
    ...(ov && ov.otherOverheads > 0
      ? [
          {
            label: 'Other costs',
            value: fmtMoney(ov.otherOverheads),
            sub: ov.items?.length ? ov.items.map((i) => `${i.name} ${fmtMoney(i.charged)}`).join(' · ') + ' · pro-rata by day' : 'pro-rata by day',
          } satisfies CostTile,
        ]
      : []),
    und && und.billPerLaunch != null
      ? { label: 'Cost / 1K launches', value: fmtMoney(und.billPerLaunch * 1000), sub: `bill ÷ launches over ${und.coveredDays} mirrored days · Airman charges ${und.airmanPerVisit != null ? fmtMoney(und.airmanPerVisit * 1000) : '—'} per 1K visits`, tone: 'accent' }
      : { label: 'Cost / 1K launches', value: '—', sub: 'needs the launcher bill' },
  ];
  return tiles;
}

export interface CostDayPoint {
  day: string;
  undetect: number;
  hosting: number;
  /** per 1,000 launches on this day; null when the day has no launches. */
  per1k: number | null;
}

/**
 * The cost-by-day series: the launcher bill per mirrored day (Airman's
 * allocation on unmirrored days), hosting per mirrored day. On the airman
 * basis - no bill mirrored - the series IS Airman's allocation per day, from
 * the report's own per-day figures (it drew nothing there before the
 * 2026-09-15 review, under a legend that said "Airman"). Other costs are
 * monthly entries charged pro-rata and are NOT drawn per day (P11): a fixed
 * cost must never read as a daily signal.
 */
export function costByDay(pl: PLReport | undefined): CostDayPoint[] {
  if (!pl) return [];
  const byDay = new Map<string, CostDayPoint>();
  const at = (day: string) => {
    let p = byDay.get(day);
    if (!p) {
      p = { day, undetect: 0, hosting: 0, per1k: null };
      byDay.set(day, p);
    }
    return p;
  };
  if (pl.undetect) {
    for (const d of pl.undetect.daily) {
      const p = at(d.day);
      p.undetect = d.flag === 'unmirrored' ? d.airmanCost : d.undetectCost;
      p.per1k = d.undetectLaunches > 0 ? (d.undetectCost / d.undetectLaunches) * 1000 : null;
    }
  } else {
    for (const d of pl.airmanDaily ?? []) at(d.day).undetect = d.cost;
  }
  for (const d of pl.hosting?.daily ?? []) at(d.day).hosting = d.cost;
  return [...byDay.values()].sort((a, b) => (a.day < b.day ? -1 : 1));
}
